package com.yendo.mx.ui.splash

import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Build
import androidx.compose.material.icons.filled.SystemUpdate
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.yendo.mx.repository.AuthRepository
import com.yendo.mx.repository.ConfigGlobalRepository
import com.yendo.mx.ui.theme.RosaYendo
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.withTimeoutOrNull

// 🚀 IMPORTS DE RECURSOS Y COMPOTTIE
import org.jetbrains.compose.resources.ExperimentalResourceApi
import yendo.shared.generated.resources.*
import io.github.alexzhirkevich.compottie.Compottie
import io.github.alexzhirkevich.compottie.LottieCompositionSpec
import io.github.alexzhirkevich.compottie.rememberLottieComposition
import io.github.alexzhirkevich.compottie.rememberLottiePainter

// 🚀 ESTADOS POSIBLES DEL SPLASH
sealed class SplashState {
    object Cargando : SplashState()
    data class Mantenimiento(val mensaje: String) : SplashState()
    data class ActualizacionRequerida(val urlTienda: String, val mensaje: String) : SplashState()
}

@OptIn(ExperimentalResourceApi::class)
@Composable
fun SplashScreen(
    onNavigateToHome: () -> Unit,
    onNavigateToAuth: () -> Unit
) {
    val isDark = isSystemInDarkTheme()
    val colorFondoBase = if (isDark) Color(0xFF121212) else Color(0xFFF8F8F8)
    val colorTextoPrimario = if (isDark) Color.White else Color.Black
    val colorTextoSecundario = if (isDark) Color(0xFFAAAAAA) else Color.Gray

    // 🚀 LÓGICA DE ANIMACIÓN FADE-IN
    var alpha by remember { mutableStateOf(0f) }
    val alphaAnim = animateFloatAsState(
        targetValue = alpha,
        animationSpec = tween(durationMillis = 800),
        label = "FadeInSplash"
    )

    var estadoSplash by remember { mutableStateOf<SplashState>(SplashState.Cargando) }
    val uriHandler = LocalUriHandler.current

    // 🚀 CARGAMOS EL ARCHIVO LOTTIE
    val composition by rememberLottieComposition {
        LottieCompositionSpec.JsonString(
            Res.readBytes("files/yendo_cargando.json").decodeToString()
        )
    }

// 🚀 MOTOR DE DECISIÓN DE SEGURIDAD (3 ANILLOS)
    LaunchedEffect(Unit) {
        alpha = 1f

        val config = withTimeoutOrNull(4000) { ConfigGlobalRepository.obtenerConfiguracionGlobal() }

        if (config != null) {
            // 🚨 REGLA 1: MANTENIMIENTO GLOBAL (Kill Switch)
            if (config.mantenimiento_activo) {
                estadoSplash = SplashState.Mantenimiento(config.mensaje_mantenimiento)
                return@LaunchedEffect 
            }

            // ⚠️ IDENTIFICACIÓN DE APLICACIÓN Y PLATAFORMA (App USUARIO)
            val metadata = com.yendo.mx.utils.obtenerMetadataDispositivo()
            val miVersionActual = metadata["version_code"]?.toLongOrNull() ?: 1L
            val esAndroid = com.yendo.mx.utils.esAndroid()
            
            val appConfig = if (esAndroid) config.app_usuario.android else config.app_usuario.ios

            // 🚨 REGLA 2: BLACKLIST (Bloqueo Quirúrgico por Bugs)
            if (appConfig.blacklisted_versions.contains(miVersionActual)) {
                estadoSplash = SplashState.ActualizacionRequerida(
                    urlTienda = appConfig.url_store,
                    mensaje = "Tu versión actual tiene un error crítico. Es obligatorio actualizar para continuar."
                )
                return@LaunchedEffect
            }

            // 🚨 REGLA 3: HARD UPDATE (Versión Mínima Requerida)
            if (miVersionActual < appConfig.min_version) {
                estadoSplash = SplashState.ActualizacionRequerida(
                    urlTienda = appConfig.url_store,
                    mensaje = "Hemos lanzado una versión nueva más rápida y segura. Actualiza para continuar."
                )
                return@LaunchedEffect
            }

            // 🟡 REGLA 4: SOFT UPDATE (Aviso de actualización sugerida pero no obligatoria)
            // 🚀 AHORA SOLO MARCA LA BANDERA, EL AVISO SE MUESTRA HASTA EL HOME
            if (miVersionActual < appConfig.latest_version) {
                com.yendo.mx.repository.ConfigGlobalRepository.urlSoftUpdate = appConfig.url_store
                com.yendo.mx.repository.ConfigGlobalRepository.mostrarSoftUpdateEnHome = true
            }
        }

        // 🚀 SI LLEGA AQUÍ, LA APP ESTÁ SEGURA PARA CORRER
        delay(2000)

        if (AuthRepository.estaLogueado()) {
            val uid = AuthRepository.obtenerUid()
            if (uid != null) {
                // 🚀 TELEMETRÍA: Reportamos la versión y hardware al sistema
                AuthRepository.reportarTelemetria(uid)
            }

            val perfil = withTimeoutOrNull(3000) {
                AuthRepository.usuarioActual.filterNotNull().first()
            }
            if (perfil != null && perfil.telefono.isNotEmpty() && perfil.nombre.isNotEmpty()) {
                onNavigateToHome()
            } else {
                onNavigateToAuth()
            }
        } else {
            onNavigateToAuth()
        }
    }

    // 🚀 DISEÑO DE LA PANTALLA SEGÚN EL ESTADO
    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(colorFondoBase),
        contentAlignment = Alignment.Center
    ) {
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            modifier = Modifier.alpha(alphaAnim.value).padding(30.dp)
        ) {
            when (val estadoActual = estadoSplash) {
                is SplashState.Cargando -> {
                    val painter = rememberLottiePainter(
                        composition = composition,
                        iterations = Compottie.IterateForever
                    )
                    Image(
                        painter = painter,
                        contentDescription = "Cargando Yendo",
                        modifier = Modifier.size(250.dp)
                    )
                }

                is SplashState.Mantenimiento -> {
                    Icon(
                        imageVector = Icons.Default.Build,
                        contentDescription = "Mantenimiento",
                        tint = RosaYendo,
                        modifier = Modifier.size(80.dp)
                    )
                    Spacer(modifier = Modifier.height(24.dp))
                    Text(
                        text = "Mantenimiento",
                        fontSize = 24.sp,
                        fontWeight = FontWeight.ExtraBold,
                        color = colorTextoPrimario
                    )
                    Spacer(modifier = Modifier.height(16.dp))
                    Text(
                        text = estadoActual.mensaje,
                        fontSize = 16.sp,
                        color = colorTextoSecundario,
                        textAlign = TextAlign.Center
                    )
                }

                is SplashState.ActualizacionRequerida -> {
                    Icon(
                        imageVector = Icons.Default.SystemUpdate,
                        contentDescription = "Actualizar App",
                        tint = RosaYendo,
                        modifier = Modifier.size(80.dp)
                    )
                    Spacer(modifier = Modifier.height(24.dp))
                    Text(
                        text = "Actualización Obligatoria",
                        fontSize = 24.sp,
                        fontWeight = FontWeight.ExtraBold,
                        color = colorTextoPrimario,
                        textAlign = TextAlign.Center
                    )
                    Spacer(modifier = Modifier.height(16.dp))
                    Text(
                        text = estadoActual.mensaje,
                        fontSize = 16.sp,
                        color = colorTextoSecundario,
                        textAlign = TextAlign.Center
                    )
                    Spacer(modifier = Modifier.height(32.dp))
                    Button(
                        onClick = {
                            try {
                                uriHandler.openUri(estadoActual.urlTienda)
                            } catch (e: Exception) {
                                println("YENDO_ERROR: No se pudo abrir la tienda")
                            }
                        },
                        colors = ButtonDefaults.buttonColors(containerColor = RosaYendo),
                        shape = RoundedCornerShape(12.dp),
                        modifier = Modifier.fillMaxWidth().height(50.dp)
                    ) {
                        Text("Ir a la Tienda", fontWeight = FontWeight.Bold, color = Color.White, fontSize = 16.sp)
                    }
                }
            }
        }
    }
}
